/**
 * Created by maksymkunytsia on 11/29/16.
 */

// Vendors
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Device, DeviceInfo } from '@core/capacitor-plugins/capacitor-plugins.class';

// Common - services
import { SecurityService } from './security.service';
import { APP_INFO } from '@shared/constants/app-info.const';
import { LocalStorage } from '../utils/localstorage.utils';
import { LogService } from './logger/log.service';
import { MyPosService } from '@core/services/system/devices/my-pos/my-pos.service';
import { PosApiService } from '../api/pos-api.service';
import { IAdyenService } from './devices/adyen/adyen.service';
import { StorageKeys, TargetDeviceEnum } from '@shared/constants';
import { ISunmiService } from '@core/services/system/devices/sunmi/sunmi.service';
import { PlatformService } from '@core/services/system/platform/platform.service';
import { DeviceSerialNumberPlugin, DeviceSerialResult } from '@paymash/capacitor-device-serial-number-plugin';
import { SunmiPrinterService } from '@core/services/system/receipt-printers/services/sunmi-printer/sunmi-printer.service';
import { NativeOnly } from '@shared/decorators/native-only.decor';

@Injectable()
export class PosInfoService {
  private devicePerformanceScore: any = null;
  private readonly logger = this.logService.createLogger('PosInfoService');

  constructor(
    public SecurityService: SecurityService,
    public platform: Platform,
    public LocalStorage: LocalStorage,
    private myPosService: MyPosService,
    private adyenService: IAdyenService,
    private posApiService: PosApiService,
    private logService: LogService,
    private sunmiService: ISunmiService,
    private platformService: PlatformService,
    private sunmiPrinterService: SunmiPrinterService
  ) {}

  public async detectedDeviceInfo(): Promise<DeviceInfo & { version: string; uuid: string; serial: string }> {
    const fullDeviceInfo: DeviceInfo & { version: string; uuid: string; serial: string } = await this.getDeviceFullInfo();
    this.saveFullDeviceInfoToStorage(fullDeviceInfo);
    return fullDeviceInfo;
  }

  public async updatePosDataOnServer() {
    const dataToUpdate = {
      deviceIdentifier: this.SecurityService.getDeviceIdentifier(),
      deviceFingerprint: this.SecurityService.getDeviceFingerprint(),
      name: await this.getDeviceModel(),
      deviceType: await this.getDeviceType(),
      // TODO ADD TOKEN
      iosNotificationsToken: null,
      appVersion: APP_INFO.APP_VERSION + ' (' + APP_INFO.APP_BUILD + ')',
    };

    const fullDeviceInfo: DeviceInfo & { version: string; uuid: string; serial: string } = await this.detectedDeviceInfo();
    const { manufacturer, isVirtual, model, platform, version, uuid, serial } = fullDeviceInfo;

    if (this.sunmiService.isSunmiDevice) {
      this.sunmiPrinterService.saveSunmiPrinterInfo().then();
    }

    const windowViewPort = window;
    this.logger.debug('updatePosDataOnServerdoSecureRequest:data', {
      data: dataToUpdate,
      device: {
        manufacturer,
        isVirtual,
        model,
        platform,
        serial,
        uuid,
        version,
        cssInnerWidth: windowViewPort?.innerWidth,
        cssInnerHeight: windowViewPort?.innerHeight,
        devicePixelRatio: windowViewPort?.devicePixelRatio,
        physicalScreenWidth: (windowViewPort?.screen?.width || 0) * (windowViewPort?.devicePixelRatio || 0),
        physicalScreenHeight: (windowViewPort?.screen?.height || 0) * (windowViewPort?.devicePixelRatio || 0),
      },
    });
    this.posApiService.posSystem(dataToUpdate).catch((err) => this.logger.error(err, 'updatePosDataOnServer:doSecureRequest'));
  }

  public async getDeviceModel(): Promise<string> {
    const deviceCapacitor: DeviceInfo | null = await Device.getInfo().catch(() => null);
    return deviceCapacitor ? (deviceCapacitor.name ? deviceCapacitor.name : deviceCapacitor.model) : 'Web';
  }

  public async getDeviceType(): Promise<string> {
    const device: DeviceInfo | null = await Device.getInfo().catch(() => null);
    return device ? device.model + ', ' + device.platform + ', ' + device.osVersion : this.platform['_ua'];
  }

  public async getDeviceFullInfo(): Promise<DeviceInfo & { version: string; uuid: string; serial: string }> {
    const fullDeviceInfo: DeviceInfo | null = await Device.getInfo().catch(() => null);
    const deviceId = await Device.getId().catch(() => ({ identifier: null }));
    const serialResult: DeviceSerialResult = await this.getSerialNumber();
    return {
      ...fullDeviceInfo,
      version: fullDeviceInfo?.osVersion,
      uuid: deviceId?.identifier,
      serial: serialResult?.serial,
    };
  }

  getDeviceFullInfoSnapshot(): DeviceInfo & { version: string; uuid: string; serial: string } {
    return this.LocalStorage.getObject(StorageKeys.fullDeviceInfo);
  }

  public performanceTest() {
    try {
      let times = [];
      const testsNum = 70;
      for (let i = 0; i < testsNum; i++) {
        const perfomanceTime = this.getPerformanceTime();
        times = [...times, perfomanceTime];
      }
      times.sort();
      times = times.slice(10, times.length - 10);
      const total = times.reduce((result, time) => result + time, 0);
      const result = Math.round(total / times.length);
      return result;
    } catch (err) {
      this.logger.error(err, 'performanceTest');
    }
  }

  public getDevicePerformance(): any {
    if (this.devicePerformanceScore) return this.devicePerformanceScore;
    return (this.devicePerformanceScore = this.LocalStorage.get('performanceTest'));
  }

  public getDevicePerformanceStatistic() {
    const isMyPosDevice: boolean = this.myPosService.isTargetDeviceMyPos;
    let devicePerfomace = this.getDevicePerformance();
    if (devicePerfomace) {
      devicePerfomace = Math.round(devicePerfomace);
      const maxScore = isMyPosDevice || this.adyenService.isAdyenDevice ? 80 : 100;
      devicePerfomace = devicePerfomace < maxScore ? devicePerfomace : maxScore;
      devicePerfomace = 101 - devicePerfomace;
      return devicePerfomace;
    }
    return 1;
  }

  public setDevicePerformance(score): void {
    if (score) {
      this.devicePerformanceScore = score;
      this.LocalStorage.set('performanceTest', '' + score);
    }
  }

  public isSlowDevice(): boolean {
    if (this.getDevicePerformance()) {
      return parseInt(this.getDevicePerformance()) > APP_INFO.APP_MIN_PERFORMANCE_SCORE;
    } else {
      return false;
    }
  }

  public getWeakDevicePageIsShowenStatus(): boolean {
    return !!this.LocalStorage.get('WeakDevicePageIsShowen');
  }

  public setWeakDevicePageIsShowedStatus(): void {
    this.LocalStorage.set('WeakDevicePageIsShowen', 'yes');
  }

  public showWeakDevicePage(): boolean {
    const isMyPosDevice: boolean = this.myPosService.isTargetDeviceMyPos;
    return this.isSlowDevice() && !this.getWeakDevicePageIsShowenStatus() && !isMyPosDevice && !this.adyenService.isAdyenDevice;
  }

  public detectedTargetDeviceModel(): TargetDeviceEnum {
    const matrix: Record<TargetDeviceEnum, boolean> = {
      [TargetDeviceEnum.ADYEN]: this.adyenService.isAdyenDevice,
      [TargetDeviceEnum.MYPOS]: this.myPosService.isMyPosDevice,
      [TargetDeviceEnum.SUNMI]: this.sunmiService.isSunmiDevice,
      [TargetDeviceEnum.ANDROID]:
        this.platformService.isAndroid &&
        !this.adyenService.isAdyenDevice &&
        !this.myPosService.isMyPosDevice &&
        !this.sunmiService.isSunmiDevice,
      [TargetDeviceEnum.IOS]: this.platformService.isIOS,
      [TargetDeviceEnum.WEB]: this.platformService.isWeb,
    };

    return Object.entries(matrix).find(([, value]: [string, boolean]): boolean => value)?.[0] as TargetDeviceEnum | undefined;
  }

  @NativeOnly({ fallback: { serial: null } })
  private async getSerialNumber(): Promise<DeviceSerialResult> {
    return await DeviceSerialNumberPlugin.getSerialDevice();
  }

  private getPerformanceTime(): number {
    const startTime = performance.now();
    for (let i = 0; i < 1000; i++) {
      const div = document.createElement('div');
      div.className = 'perf-test';
      div.innerHTML = '<p>This is test</p>';
    }
    return performance.now() - startTime;
  }

  private saveFullDeviceInfoToStorage(info: DeviceInfo & { version: string; uuid: string; serial: string }): void {
    try {
      this.LocalStorage.setObject(StorageKeys.fullDeviceInfo, info);
    } catch (e) {
      this.logger.error(e, 'saveFullDeviceInfoToStorage');
    }
  }
}
